import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Reveal a heading word-by-word with a soft blur->sharp fade.
 * Splits textContent into <span class="word"> elements once.
 */
export const revealWords = (
  el: HTMLElement | null,
  options: { stagger?: number; y?: number; trigger?: Element | null; start?: string } = {}
) => {
  if (!el || prefersReducedMotion()) return;
  if (el.dataset.wordsSplit === "1") return;

  const text = el.textContent || "";
  const words = text.split(/\s+/).filter(Boolean);
  el.textContent = "";
  const frag = document.createDocumentFragment();
  words.forEach((w, i) => {
    const span = document.createElement("span");
    span.className = "reveal-word";
    span.textContent = w;
    span.style.display = "inline-block";
    span.style.willChange = "transform, opacity, filter";
    frag.appendChild(span);
    if (i < words.length - 1) frag.appendChild(document.createTextNode(" "));
  });
  el.appendChild(frag);
  el.dataset.wordsSplit = "1";

  const wordEls = el.querySelectorAll<HTMLElement>(".reveal-word");
  gsap.fromTo(
    wordEls,
    { y: options.y ?? 30, opacity: 0, filter: "blur(8px)" },
    {
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      duration: 0.7,
      stagger: options.stagger ?? 0.06,
      ease: "power2.out",
      scrollTrigger: {
        trigger: options.trigger ?? el,
        start: options.start ?? "top 85%",
        once: true,
      },
      onComplete: () => wordEls.forEach((w) => (w.style.willChange = "auto")),
    }
  );
};

/**
 * Batch reveal a collection of items efficiently.
 */
export const revealBatch = (
  els: Element[] | NodeListOf<Element>,
  options: { y?: number; rotation?: number; stagger?: number; duration?: number } = {}
) => {
  if (!els || (els as ArrayLike<Element>).length === 0) return;
  if (prefersReducedMotion()) {
    gsap.set(els, { opacity: 1, y: 0, rotation: 0 });
    return;
  }
  gsap.set(els, {
    y: options.y ?? 50,
    opacity: 0,
    rotation: options.rotation ?? 0,
    willChange: "transform, opacity",
  });
  ScrollTrigger.batch(els as Element[], {
    start: "top 88%",
    onEnter: (batch) =>
      gsap.to(batch, {
        y: 0,
        opacity: 1,
        rotation: 0,
        duration: options.duration ?? 0.7,
        stagger: options.stagger ?? 0.1,
        ease: "power2.out",
        force3D: true,
        onComplete: () => batch.forEach((b) => ((b as HTMLElement).style.willChange = "auto")),
      }),
  });
};

/**
 * Vertical parallax on an element while its container scrolls past viewport.
 */
export const parallaxY = (
  el: HTMLElement | null,
  options: { amount?: number; trigger?: Element | null } = {}
) => {
  if (!el || prefersReducedMotion()) return;
  gsap.to(el, {
    yPercent: options.amount ?? 15,
    ease: "none",
    scrollTrigger: {
      trigger: options.trigger ?? el,
      start: "top bottom",
      end: "bottom top",
      scrub: true,
    },
  });
};

/**
 * Counter animation: animate textContent from 0 to a numeric target.
 * Detects numeric portion and preserves prefix/suffix (e.g., "+", "let", "%").
 */
export const countUp = (el: HTMLElement | null) => {
  if (!el) return;
  const original = el.textContent?.trim() || "";
  const match = original.match(/(\D*)(\d+(?:[.,]\d+)?)(.*)/);
  if (!match) return;
  const [, prefix, numStr, suffix] = match;
  const target = parseFloat(numStr.replace(",", "."));
  if (Number.isNaN(target)) return;
  const decimals = (numStr.split(/[.,]/)[1] || "").length;

  if (prefersReducedMotion()) {
    el.textContent = original;
    return;
  }

  const obj = { v: 0 };
  el.textContent = `${prefix}0${suffix}`;
  gsap.to(obj, {
    v: target,
    duration: 1.6,
    ease: "power2.out",
    scrollTrigger: { trigger: el, start: "top 88%", once: true },
    onUpdate: () => {
      const formatted = decimals > 0 ? obj.v.toFixed(decimals) : Math.round(obj.v).toString();
      el.textContent = `${prefix}${formatted}${suffix}`;
    },
    onComplete: () => {
      el.textContent = original;
    },
  });
};
